/**
 * Circular crop geometry.
 *
 * A crop is stored independently of any pixel size, so the same transform drives the
 * little preview circle, the full-screen editor, and the final export without any of
 * them having to agree on a resolution.
 */

export interface CropTransform {
  /** Multiplier on the cover-fit scale. 1 means the image exactly covers the circle. */
  zoom: number;
  /** Pan in units of the circle's radius. 0 is centred. */
  panX: number;
  panY: number;
}

export const DEFAULT_CROP: CropTransform = { zoom: 1, panX: 0, panY: 0 };

export const MIN_ZOOM = 1;
export const MAX_ZOOM = 6;

export interface Placement {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * How far the image may pan before an edge would enter the circle, in radius units.
 *
 * Independent of the circle's pixel size: at cover fit the tight dimension has no
 * slack at all, and the loose one has exactly the overhang from the aspect mismatch.
 */
export function panLimits(
  imageWidth: number,
  imageHeight: number,
  zoom: number,
): { x: number; y: number } {
  const aspect = imageWidth / imageHeight;
  const wide = aspect >= 1;
  // Relative to the circle diameter, after cover-fitting and zooming.
  const w = (wide ? aspect : 1) * zoom;
  const h = (wide ? 1 : 1 / aspect) * zoom;
  return { x: Math.max(0, w - 1), y: Math.max(0, h - 1) };
}

/** Pulls a transform back into legal range, so the circle can never show empty space. */
export function clampCrop(
  imageWidth: number,
  imageHeight: number,
  t: CropTransform,
): CropTransform {
  const zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, t.zoom));
  const limit = panLimits(imageWidth, imageHeight, zoom);
  // The `+ 0` normalises -0, which Object.is treats as distinct from 0 and would
  // make two otherwise-identical crops compare unequal.
  return {
    zoom,
    panX: Math.min(limit.x, Math.max(-limit.x, t.panX)) + 0,
    panY: Math.min(limit.y, Math.max(-limit.y, t.panY)) + 0,
  };
}

/**
 * Where to draw the source image so it fills a circle of the given diameter.
 * The circle is assumed to occupy the box from (0,0) to (size,size).
 */
export function placeImage(
  imageWidth: number,
  imageHeight: number,
  size: number,
  t: CropTransform,
): Placement {
  const cover = Math.max(size / imageWidth, size / imageHeight);
  const scale = cover * t.zoom;
  const width = imageWidth * scale;
  const height = imageHeight * scale;
  const half = size / 2;
  return {
    x: half + t.panX * half - width / 2,
    y: half + t.panY * half - height / 2,
    width,
    height,
  };
}

/** True when the placed image fully covers the circle's bounding box. */
export function covers(placement: Placement, size: number): boolean {
  const eps = 1e-6;
  return (
    placement.x <= eps &&
    placement.y <= eps &&
    placement.x + placement.width >= size - eps &&
    placement.y + placement.height >= size - eps
  );
}

/**
 * Converts a drag in pixels into a pan delta. Panning is expressed in radius units,
 * so a drag has to be divided by the circle's radius to stay proportional at any size.
 */
export function panByPixels(
  t: CropTransform,
  dxPixels: number,
  dyPixels: number,
  size: number,
): CropTransform {
  const half = size / 2;
  return { ...t, panX: t.panX + dxPixels / half, panY: t.panY + dyPixels / half };
}

/**
 * Zooms toward a point, keeping whatever is under that point roughly fixed — the
 * behaviour you expect from pinch-zoom and scroll-wheel zoom.
 *
 * `focusX/Y` are pixel offsets from the circle's centre.
 */
export function zoomAt(
  t: CropTransform,
  factor: number,
  focusX: number,
  focusY: number,
  size: number,
): CropTransform {
  const zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, t.zoom * factor));
  const applied = zoom / t.zoom;
  const half = size / 2;
  // The focus point sits at this offset from the image centre, in radius units;
  // scaling the image moves it by (applied - 1) of that, so cancel the difference.
  const fx = focusX / half;
  const fy = focusY / half;
  return {
    zoom,
    panX: t.panX - (fx - t.panX) * (applied - 1),
    panY: t.panY - (fy - t.panY) * (applied - 1),
  };
}
